/**
 * SRP-6a (RFC 5054) クライアント側ラッパ。
 *
 * apps/api 側 (secure-remote-password/server) と **完全互換** のフローを提供する。
 * 暗号一次関数はライブラリ任せで、型安全な API + ドメイン固有の例外型 + 監査ログ抑止を被せる。
 *
 * 用途 (KEY_HIERARCHY.md §4.1, §4.2):
 *
 *   登録:
 *     salt        = generateSrpSalt()
 *     privateKey  = deriveSrpPrivateKey(salt, username, password)
 *     verifier    = deriveSrpVerifier(privateKey)
 *     → サーバへ { srpSalt, srpVerifier } を送信
 *
 *   ログイン:
 *     ephemeral   = generateClientEphemeral()                       // { secret: a, public: A }
 *     session     = deriveClientSession(a, B, salt, username, x)    // { key: K, proof: M1 }
 *     → サーバへ { A, M1, sessionId } を送信
 *     verifyServerProof(A, session, M2)                              // 失敗で SrpVerificationError
 *
 * セキュリティ前提:
 *   - 入力 password は呼び出し直後に GC 可能なスコープに限定する (zeroize 不可な string)
 *   - private key (x), session key (K), proof (M1/M2) は派生後すぐ
 *     呼び出し側が破棄すること (本ラッパでは hex string として返却するため、参照を保持しない)
 *   - 例外メッセージに password / verifier / private key を含めない
 */
import * as srpClient from 'secure-remote-password/client'

/** RFC 5054 における "I" (Identity)。OoKey では小文字メールアドレスを使う。 */
export type SrpUsername = string

/** 16 byte 以上の hex 文字列 (ライブラリは hex 文字列で扱う) */
export type SrpSalt = string

/** SRP-6a verifier (g^x mod N) — hex */
export type SrpVerifier = string

/** Client / Server ephemeral 公開鍵 (A / B) — hex */
export type SrpPublic = string

/** Client / Server ephemeral 秘密鍵 (a / b) — hex。**サーバ B は client が保持しない** */
export type SrpSecret = string

/** SRP "x" — hex (= Argon2 output と混同しないようブランド) */
export type SrpPrivateKey = string

/** クライアント session 派生結果 (K + M1) */
export interface SrpClientSession {
  /** 共有 session key K (hex)。Phase 4 で対称鍵として再利用検討。 */
  key: string

  /** クライアント proof M1 (hex) — サーバへ送る */
  proof: string
}

/** SRP プロトコル違反の統一例外。M1/M2 検証失敗等。 */
export class SrpVerificationError extends Error {
  public constructor(message: string = 'SRP verification failed') {
    super(message)
    this.name = 'SrpVerificationError'
  }
}

// ── 登録フロー ──────────────────────────────────────────────────

/** 16 byte の salt を生成して hex で返す (ライブラリ既定)。 */
export const generateSrpSalt = (): SrpSalt => srpClient.generateSalt()

/**
 * x = H(salt, H(username:password)) を計算する (RFC 5054 標準形)。
 *
 * セキュリティ補足: ここで返す x は SHA-256 ベース (Argon2 ではない) のため、
 * verifier が漏洩した場合のオフライン攻撃耐性は MP のエントロピー次第。
 * Phase 4 hardening で MK (Argon2 出力) を password の代わりに渡す
 * Bitwarden 流の強化を検討する余地あり (要 KEY_HIERARCHY.md 改訂)。
 */
export const deriveSrpPrivateKey = (
  salt: SrpSalt,
  username: SrpUsername,
  password: string,
): SrpPrivateKey => srpClient.derivePrivateKey(salt, username, password)

/** v = g^x mod N */
export const deriveSrpVerifier = (privateKey: SrpPrivateKey): SrpVerifier =>
  srpClient.deriveVerifier(privateKey)

// ── ログインフロー ──────────────────────────────────────────────

/** クライアント ephemeral (a, A) を生成する */
export const generateClientEphemeral = (): {
  secret: SrpSecret
  public: SrpPublic
} => srpClient.generateEphemeral()

/**
 * クライアント session を派生する。
 * 内部で M1 (= H(A, B, K)) を計算する。
 *
 * @throws RangeError — server の B が無効値の場合 (B === 0 mod N など)
 */
export const deriveClientSession = (
  clientSecretEphemeral: SrpSecret,
  serverPublicEphemeral: SrpPublic,
  salt: SrpSalt,
  username: SrpUsername,
  privateKey: SrpPrivateKey,
): SrpClientSession =>
  srpClient.deriveSession(clientSecretEphemeral, serverPublicEphemeral, salt, username, privateKey)

/**
 * サーバの M2 (= H(A, M1, K)) を検証する。
 *
 * 失敗時はライブラリが Error を throw するので、ここで `SrpVerificationError`
 * に変換して呼び出し側のハンドリングを統一する。
 * **error.message に session key K や M2 を含めない** (ライブラリが含めないことを確認済)。
 */
export const verifyServerProof = (
  clientPublicEphemeral: SrpPublic,
  clientSession: SrpClientSession,
  serverSessionProof: string,
): void => {
  try {
    srpClient.verifySession(clientPublicEphemeral, clientSession, serverSessionProof)
  } catch (e) {
    // 内訳開示しない (情報漏洩・タイミング攻撃緩和)
    throw new SrpVerificationError(
      e instanceof Error ? `srp: ${e.name}` : 'SRP verification failed',
    )
  }
}
